import React from 'react';
import { Radio, RadioGroup } from '@headlessui/react';

import { setNoiseSuppressionEngine } from 'flow/actions/headset-actions';
import BetaBadge from 'flow/components/badges/BetaBadge';
import Icon from 'flow/components/ui-components/Icon';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectLocalCallIsOngoing } from 'flow/selectors/call-selectors';
import { selectNoiseSuppressionEngine } from 'flow/selectors/headset-selectors';
import {
    ActiveNoiseSuppressionEngine,
    DEFAULT_NOISE_SUPPRESSION_ENGINE,
    isNoiseSuppressionSupported,
    NoiseSuppressionEngine,
} from 'flow/utils/noise-suppression-utils';

import SettingRow from './SettingRow';
import SettingSection from './SettingSection';

type EngineOption = {
    engine: ActiveNoiseSuppressionEngine;
    labelKey: string;
    descriptionKey: string;
};

/* Two models rather than one, because the open question this setting exists to
   answer is whether an open-source denoiser is good enough to replace the Krisp
   licences some customers pay for — and that is a question about the strong
   model, not the cheap one. */
const ENGINE_OPTIONS: EngineOption[] = [
    {
        engine: 'rnnoise',
        labelKey: 'media.settings.noisesuppression.standard',
        descriptionKey: 'media.settings.noisesuppression.standard_description',
    },
    {
        engine: 'gtcrn',
        labelKey: 'media.settings.noisesuppression.strong',
        descriptionKey: 'media.settings.noisesuppression.strong_description',
    },
];

const NoiseSuppressionEngineRow = ({
    engine,
    labelKey,
    descriptionKey,
}: EngineOption) => {
    const { i18next } = useAppContext();
    const selectedEngine = useAppSelector(selectNoiseSuppressionEngine);
    const callIsOngoing = useAppSelector(selectLocalCallIsOngoing);

    /* The radio is only the label text, but its stretched pseudo-element covers
       the row that `relative` anchors, so a click anywhere in the row, hint
       included, selects it. The radio sits inside SettingRow's Field, which is
       what makes the hint its description; the check icon in `cta` stays the
       visual state. */
    return (
        <SettingRow
            className="relative"
            disabled={callIsOngoing}
            information={i18next.t(descriptionKey)}
            cta={engine === selectedEngine ? <Icon iconKey="done" /> : null}
        >
            <Radio
                value={engine}
                className="stretched-control cursor-pointer data-disabled:cursor-not-allowed"
            >
                {i18next.t(labelKey)}
            </Radio>
        </SettingRow>
    );
};

const NoiseSuppressionToggle = () => {
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();
    const engine = useAppSelector(selectNoiseSuppressionEngine);
    const callIsOngoing = useAppSelector(selectLocalCallIsOngoing);

    /* The graph is built from the track the call already sends, at `confirmed`,
       so a change made now reaches nothing that is up. Say that in the hint
       rather than accept a switch that appears to do something. */
    const information = callIsOngoing
        ? 'media.settings.noisesuppression.locked_during_call'
        : 'media.settings.noisesuppression_description';

    return (
        <SettingRow
            disabled={callIsOngoing}
            information={i18next.t(information)}
            cta={
                <Toggle
                    value={engine !== 'off'}
                    disabled={callIsOngoing}
                    text={i18next.t('media.settings.noisesuppression')}
                    onChange={(enabled) =>
                        dispatch(
                            setNoiseSuppressionEngine(
                                enabled
                                    ? DEFAULT_NOISE_SUPPRESSION_ENGINE
                                    : 'off',
                            ),
                        )
                    }
                />
            }
        >
            {i18next.t('media.settings.noisesuppression')}
            <BetaBadge />
        </SettingRow>
    );
};

const NoiseSuppressionEngineChoice = () => {
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();
    const selectedEngine = useAppSelector(selectNoiseSuppressionEngine);
    const callIsOngoing = useAppSelector(selectLocalCallIsOngoing);

    return (
        <>
            <SettingSection>
                {i18next.t('media.settings.noisesuppression.quality')}
            </SettingSection>
            <RadioGroup
                value={selectedEngine}
                disabled={callIsOngoing}
                aria-label={i18next.t(
                    'media.settings.noisesuppression.quality',
                )}
                onChange={(engine: NoiseSuppressionEngine) =>
                    dispatch(setNoiseSuppressionEngine(engine))
                }
            >
                {ENGINE_OPTIONS.map((option) => (
                    <NoiseSuppressionEngineRow
                        key={option.engine}
                        {...option}
                    />
                ))}
            </RadioGroup>
        </>
    );
};

const NoiseSuppression = () => {
    const { i18next } = useAppContext();
    const engine = useAppSelector(selectNoiseSuppressionEngine);

    /* Nothing to offer where the browser has no AudioWorklet to run the model
       in — the call still works, it just keeps the browser's own suppressor. */
    if (!isNoiseSuppressionSupported()) {
        return null;
    }

    return (
        <>
            <SettingSection>
                {i18next.t('media.settings.noisesuppression')}
            </SettingSection>
            <NoiseSuppressionToggle />
            {engine !== 'off' ? <NoiseSuppressionEngineChoice /> : null}
        </>
    );
};

export default NoiseSuppression;
